import React, {
  useState, useCallback, useMemo, Suspense,
} from 'react'
import { createPortal } from 'react-dom'
import { ErrorBoundary } from 'react-error-boundary'
import CommunityErrorFallback from '../../../../shared/ErrorHandling/CommunityErrorFallback/index'
import { connect as connectStaticText } from '../../../../../StaticText/connect'
import {
  StaticTextProvider,
  useTranslatedText,
  InternationalStrings,
} from '../../../../../StaticText/StaticTextContext'
import { usePostHogEvent } from '../../../../../PostHog/index'
import PlayOutline from '../../../../../Icon/icons/PlayOutline/index'
import {
  type EditToolbarLabels,
  type CarouselItem,
} from '../../../shared/CommunityProfileCarousels/index'
import { useHistory } from '../../../../../Router/index'
import { historyRedirect } from '../../../../../../services/navigation/index'
import { URL_AI_CHAT, URL_TOPICS } from '../../../../../../services/url/constants'
import { isBrowser } from '../../../../../../config/environment'
import type {
  EnrichedPlaylistItem,
  EnrichedInProgressItem,
  PlaylistShell,
  PlaylistVisibilityLevel,
} from '../../../../../../services/community/types'
import PlaylistLibReviewBanner from '../../PlaylistLibReviewBanner/index'
import MyPlaylistRow from '../../../shared/PlaylistRow/MyPlaylistRow'
import EditableMyPlaylistRow from '../../../shared/PlaylistRow/EditableMyPlaylistRow'
import SystemPlaylistShellRow from '../../../shared/PlaylistRow/SystemPlaylistShellRow'
import SystemPlaylistExternalRow from '../../../shared/PlaylistRow/SystemPlaylistExternalRow'
import CommunityEmptyState from '../../../shared/CommunityEmptyState/CommunityEmptyState'
import PlaylistMenuStaticTextScope from '../../ProfilePlaylistMenuOptions/PlaylistMenuStaticTextScope'

const DeletePlaylistConfirmModal = React.lazy(() => import('../../../../Playlist/shared/DeletePlaylistConfirmModal/index'))

export interface ProfileMyPlaylistsProps {
  inProgressItems?: EnrichedInProgressItem[]
  /**
   * Combined loading flag for the Currently Watching row — true while
   * either the upstream carousel query or `useInProgressEnrichment`'s
   * internal title-group hydration is in flight. Drives the always-mounted
   * skeleton path so the row never pops in. Defaults to `false`.
   */
  inProgressIsHydrating?: boolean
  /**
   * Time-weighted "{percent}% overall" stat for the Currently Watching row.
   * Computed upstream by `useInProgressEnrichment` from the raw (pre-dedup)
   * in-progress positions — see that hook's docblock for the formula.
   * Passed as a prop instead of derived locally so the calc sees the
   * pre-dedup data; `inProgressItems` above is already deduped.
   * Defaults to `0` when the upstream is undefined or has no usable
   * duration to divide by.
   */
  inProgressOverallProgress?: number
  playlists?: PlaylistShell[]
  /** Watch Later / Favorites shell entities — variant hydrates per-row. */
  defaultPlaylist?: PlaylistShell | null
  favoritesPlaylist?: PlaylistShell | null
  searchQuery?: string
  searchResultIds?: Set<string> | null
  language?: string
  jwt?: string
  onUpdateVisibility?: ((id: string, vis: PlaylistVisibilityLevel) => void) | null
  onPlaylistAction?: ((
    playlistId: string,
    action: string,
    playlistType?: string,
    playlistName?: string,
  ) => void) | null
  onRemoveItem?: ((
    playlistId: string,
    playlistType: string,
    item: EnrichedPlaylistItem | EnrichedInProgressItem,
  ) => void) | null
  onReorderItem?: ((
    playlistId: string,
    playlistType: string,
    item: EnrichedPlaylistItem,
    newPosition: number,
  ) => void) | null
  onHideInProgressItem?: ((
    playlistId: string,
    playlistType: string,
    item: EnrichedPlaylistItem | EnrichedInProgressItem,
  ) => void) | null
  allowedVisibilities?: readonly PlaylistVisibilityLevel[]
  onRemoveWatchLaterItem?: ((item: EnrichedPlaylistItem) => void) | null
  onReorderWatchLaterItem?: ((item: EnrichedPlaylistItem, newPosition: number) => void) | null
  onRemoveFavoritesItem?: ((item: EnrichedPlaylistItem) => void) | null
  onReorderFavoritesItem?: ((item: EnrichedPlaylistItem, newPosition: number) => void) | null
  /** Pre-enriched Watch History items — Phase 6 swap retains this prop. */
  watchHistoryItems?: EnrichedPlaylistItem[]
  watchHistoryTotalCount?: number
  watchHistoryTotalDuration?: string | null
  onRemoveWatchHistoryItem?: ((item: EnrichedPlaylistItem) => void) | null
  isPreview?: boolean
  isProfilePrivate?: boolean
  loggedInUuid?: string
  staticText: InternationalStrings
}

type ProfileMyPlaylistsContentProps = Omit<ProfileMyPlaylistsProps, 'staticText'>

function ProfileMyPlaylistsContent (props: ProfileMyPlaylistsContentProps) {
  const {
    inProgressItems = [],
    inProgressIsHydrating = false,
    inProgressOverallProgress = 0,
    playlists = [],
    defaultPlaylist = null,
    favoritesPlaylist = null,
    searchQuery = '',
    searchResultIds = null,
    language = 'en',
    jwt,
    onUpdateVisibility = null,
    onPlaylistAction = null,
    onRemoveItem = null,
    onReorderItem = null,
    onHideInProgressItem = null,
    allowedVisibilities,
    onRemoveWatchLaterItem = null,
    onReorderWatchLaterItem = null,
    onRemoveFavoritesItem = null,
    onReorderFavoritesItem = null,
    watchHistoryItems = [],
    watchHistoryTotalCount = 0,
    watchHistoryTotalDuration = null,
    onRemoveWatchHistoryItem = null,
    isPreview = false,
    isProfilePrivate = false,
    loggedInUuid,
  } = props

  const [editingPlaylistId, setEditingPlaylistId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState<string | null>(null)
  const [pendingDelete, setPendingDelete] = useState<{
    playlistId: string
    playlistType: string
    playlistName: string
    itemCount: number
  } | null>(null)

  // Page-level i18n labels (per-row labels resolved here and threaded down
  // so variants stay pure-prop — direct StaticTextContext subscription would
  // bypass React.memo on context updates).
  const seeMoreLabel = useTranslatedText({ keys: ['seeMore'] })
  const showLessLabel = useTranslatedText({ keys: ['showLess'] })
  const watchLaterLabel = useTranslatedText({ keys: ['watchLater'] })
  const favoritesLabel = useTranslatedText({ keys: ['favorites'] })
  const watchHistoryLabel = useTranslatedText({ keys: ['watchHistory'] })
  const currentlyWatchingLabel = useTranslatedText({ keys: ['currentlyWatching'] })
  const seriesCountTemplate = useTranslatedText({ keys: ['seriesCount'] })
  const overallProgressTemplate = useTranslatedText({ keys: ['overallProgress'] })
  const noPlaylistsText = useTranslatedText({ keys: ['noPlaylists'] })
  const moveEarlierLabel = useTranslatedText({ keys: ['moveEarlierInPlaylist'] })
  const moveLaterLabel = useTranslatedText({ keys: ['moveLaterInPlaylist'] })
  const removeFromPlaylistLabel = useTranslatedText({ keys: ['removeFromPlaylist'] })
  const confirmRemoveFromPlaylistLabel = useTranslatedText({ keys: ['confirmRemoveFromPlaylist'] })
  const parentSeeEntireTemplate = useTranslatedText({ keys: ['seeEntirePlaylist'] })
  const parentDoneLabel = useTranslatedText({ keys: ['done'] })
  const parentZeroVideosLabel = useTranslatedText({ keys: ['zeroVideos'] })
  const parentZeroDurationLabel = useTranslatedText({ keys: ['zeroDuration'] })
  const emptyCalloutText = useTranslatedText({ keys: ['emptyPlaylistCallout'] })
  const whatNextLabel = useTranslatedText({ keys: ['whatShouldIWatchNext'] })
  const browseGaiaLibraryLabel = useTranslatedText({ keys: ['browseGaiaLibrary'] })

  // ── Handlers ────────────────────────────────────────────────────────
  const history = useHistory()

  const handleWhatNextClick = useCallback((prompt: string) => {
    if (!history) return
    historyRedirect({ history, url: URL_AI_CHAT, query: { prompt } })
  }, [history])

  const handleBrowseClick = useCallback(() => {
    if (!history) return
    historyRedirect({ history, url: URL_TOPICS })
  }, [history])

  // O(1) id → shell lookup so menu-action handlers (delete-confirm reads
  // itemCount) don't linear-scan `playlists` on every click.  Declared
  // before `handlePlaylistAction` below so its closure can capture it.
  const playlistsById = useMemo(() => {
    const map = new Map<string, PlaylistShell>()
    playlists.forEach((p) => { map.set(p.id, p) })
    return map
  }, [playlists])

  // Shuffle lives at the row level (the row has hydrated items locally via
  // useRowHydration). The menu's onAction wiring keeps a path for shuffle
  // if a variant ever re-enables it; for now it falls through to the
  // upstream handler which intentionally ignores 'shuffle'.

  const handlePlaylistAction = useCallback((
    playlistId: string,
    action: string,
    playlistType?: string,
    playlistName?: string,
  ) => {
    if (action === 'edit') {
      if (editingPlaylistId === playlistId) {
        setEditingPlaylistId(null)
      } else {
        setEditingPlaylistId(playlistId)
        setEditingName(playlistName || '')
      }
      return
    }
    if (action === 'delete') {
      // delete-confirm reads itemCount from the SHELL — authoritative
      // regardless of hydration state.
      const itemCount = playlistsById.get(playlistId)?.itemCount ?? 0
      setPendingDelete({
        playlistId,
        playlistType: playlistType || '',
        playlistName: playlistName || '',
        itemCount,
      })
      return
    }
    // 'share' is intentionally absent — PlaylistShareButton on each row owns
    // its own modal state, so the menu no longer needs to dispatch.
    if (onPlaylistAction) {
      onPlaylistAction(playlistId, action, playlistType, playlistName)
    }
  }, [onPlaylistAction, playlistsById, editingPlaylistId])

  const handleNameChange = useCallback((value: string) => {
    setEditingName(value)
  }, [])

  const handleDoneEditing = useCallback((
    playlistId: string,
    playlistType?: string,
    originalName?: string,
  ) => {
    if (
      editingName != null
      && editingName !== originalName
      && onPlaylistAction
    ) {
      onPlaylistAction(playlistId, 'rename', playlistType, editingName)
    }
    setEditingPlaylistId(null)
    setEditingName(null)
  }, [onPlaylistAction, editingName])

  const trackPlaylistDeleted = usePostHogEvent('community_playlist_deleted')
  const handleConfirmDelete = useCallback(() => {
    if (pendingDelete && onPlaylistAction) {
      onPlaylistAction(
        pendingDelete.playlistId,
        'delete',
        pendingDelete.playlistType,
        pendingDelete.playlistName,
      )
      trackPlaylistDeleted(pendingDelete.playlistId)
    }
    setPendingDelete(null)
  }, [pendingDelete, onPlaylistAction, trackPlaylistDeleted])

  const handleCancelDelete = useCallback(() => {
    setPendingDelete(null)
  }, [])

  const handleDoneSystemEdit = useCallback(() => {
    setEditingPlaylistId(null)
    setEditingName(null)
  }, [])

  const handleHideCurrentlyWatching = useCallback((item: CarouselItem) => {
    if (onHideInProgressItem) {
      onHideInProgressItem(
        'currently-watching',
        'default',
        item as EnrichedPlaylistItem | EnrichedInProgressItem,
      )
    }
  }, [onHideInProgressItem])

  // Watch History remove handler — mirrors `handleHideCurrentlyWatching`
  // above. The upstream `useProfilePlaylists.handleHideWatchHistoryItem`
  // fires `FLAG_WATCHED_VIDEO` with `hiddenByUser: true` to drop the row
  // from the user's history — same product behavior as
  // `handleHideInProgressItem` for Currently Watching.
  const handleRemoveWatchHistory = useCallback((item: CarouselItem) => {
    if (onRemoveWatchHistoryItem) {
      onRemoveWatchHistoryItem(item as EnrichedPlaylistItem)
    }
  }, [onRemoveWatchHistoryItem])

  // Custom playlist remove handler: forwards to upstream with the playlist id
  // and type the variant carries on its props. (Variants thread per-row.)
  const handleRemoveCustomItem = useCallback((
    playlistId: string,
    playlistType: string,
    item: EnrichedPlaylistItem,
  ) => {
    if (onRemoveItem) onRemoveItem(playlistId, playlistType, item)
  }, [onRemoveItem])

  // ── Currently Watching custom stats ─────────────────────────────────
  // The "{percent}% overall" stat is computed upstream in
  // `useInProgressEnrichment` from the RAW (pre-dedup) in-progress
  // positions (time-weighted `round(100 * sum(position) / sum(duration))`)
  // and threaded down via `inProgressOverallProgress`. We can't compute it
  // here because `inProgressItems` is post-dedup — episodes 2+ of any
  // in-progress series have already been collapsed by the time the array
  // reaches this component.

  // The "{count} series" label counts DISTINCT series — not raw in-progress
  // items. `useInProgressEnrichment` emits one entry per in-progress media
  // position, then `buildEpisodeOfSeriesTile` pivots each to a series-shaped
  // tile whose `tileData.contentId` is the parent series. Two episodes of
  // the same series therefore produce two enriched entries with the same
  // `tileData.contentId`; the user sees them as one series tile visually,
  // so the stat must dedupe by series identity. Standalone in-progress
  // videos (no parent series) keep `tileData.contentType === 'video'` and
  // are intentionally excluded — they are not "series" per the label.
  // See `buildEpisodeOfSeriesTile` in content-utils.ts and the precedent
  // in watchHistoryEnrichment.ts:125-133.
  const distinctSeriesCount = useMemo(() => {
    if (!inProgressItems || inProgressItems.length === 0) return 0
    const seen = new Set<string>()
    inProgressItems.forEach((item) => {
      if (item.tileData?.contentType !== 'series') return
      const key = String(item.tileData?.contentId ?? '')
      if (key) seen.add(key)
    })
    return seen.size
  }, [inProgressItems])

  const currentlyWatchingCustomStats = useMemo(() => {
    const hasItems = inProgressItems && inProgressItems.length > 0
    // Three states (mirrors the see-entire-link three-state pattern):
    //   (a) items resolved → real stats
    //   (b) hydration in flight, items not yet here → transparent placeholder
    //       with the SAME DOM shape so the resolved swap is opacity-only and
    //       the row height stays stable (CLS fix for desktop 0.0280 shift).
    //   (c) hydration settled, still no items → return undefined; the row's
    //       empty-state path takes over and the stats slot is hidden.
    if (!hasItems && !inProgressIsHydrating) return undefined
    const isPlaceholder = !hasItems
    const statClassName = `profile-my-playlists__playlist-stat${
      isPlaceholder ? ' profile-my-playlists__playlist-stat--placeholder' : ''
    }`
    return (
      <div className="profile-my-playlists__playlist-stats">
        <span className={statClassName} aria-hidden={isPlaceholder || undefined}>
          <PlayOutline aria-hidden={isPlaceholder || undefined} />
          {seriesCountTemplate.replace('{count}', String(distinctSeriesCount))}
        </span>
        <span className={statClassName} aria-hidden={isPlaceholder || undefined}>
          {overallProgressTemplate.replace('{percent}', String(inProgressOverallProgress))}
        </span>
      </div>
    )
  }, [
    inProgressItems,
    inProgressIsHydrating,
    distinctSeriesCount,
    seriesCountTemplate,
    overallProgressTemplate,
    inProgressOverallProgress,
  ])

  // In-row empty state for Currently Watching — rendered by
  // `PlaylistRow.Body` when the row is mounted, no items are present, and
  // hydration has finished. The skeleton branch handles the loading window
  // so this only shows after both loading paths resolve to empty.
  //
  // Reuses the standard empty-playlist treatment (same `compact` variant,
  // copy, and CTAs that Watch Later / Favorites / custom rows render when
  // empty): description = `emptyPlaylistCallout`, primary CTA opens the AI
  // Guide pre-seeded with "What should I watch next?", secondary CTA
  // navigates to the topic library — matches `MyPlaylistRow.tsx:174-187`.
  const currentlyWatchingEmptyState = useMemo(() => (
    <CommunityEmptyState
      variant="compact"
      description={emptyCalloutText}
      primaryCta={{
        label: whatNextLabel,
        onClick: handleWhatNextClick,
      }}
      secondaryCta={{
        label: browseGaiaLibraryLabel,
        onClick: handleBrowseClick,
      }}
    />
  ), [
    emptyCalloutText,
    whatNextLabel,
    browseGaiaLibraryLabel,
    handleWhatNextClick,
    handleBrowseClick,
  ])

  // ── Filtering ───────────────────────────────────────────────────────
  // Search matches against shell-side playlist name + the upstream
  // `searchResultIds` set (server-side search contributes that set).
  const filteredPlaylists = useMemo(() => {
    if (!searchQuery) return playlists
    const q = searchQuery.toLowerCase()
    return playlists.filter((p) => {
      if (searchResultIds && searchResultIds.has(p.id)) return true
      if ((p.playlistName || '').toLowerCase().includes(q)) return true
      return false
    })
  }, [playlists, searchQuery, searchResultIds])

  // ── Edit-toolbar labels (one stable bundle threaded down) ───────────
  const editToolbarLabels = useMemo<EditToolbarLabels>(() => ({
    earlierLabel: moveEarlierLabel,
    laterLabel: moveLaterLabel,
    removeLabel: removeFromPlaylistLabel,
    confirmLabel: confirmRemoveFromPlaylistLabel,
  }), [
    moveEarlierLabel, moveLaterLabel,
    removeFromPlaylistLabel, confirmRemoveFromPlaylistLabel,
  ])

  // System-row label bundle for the compact empty-state CTA on Watch Later
  // / Favorites.
  const systemEmptyStateLabels = useMemo(() => ({
    description: emptyCalloutText,
    whatNext: whatNextLabel,
    browseGaiaLibrary: browseGaiaLibraryLabel,
  }), [emptyCalloutText, whatNextLabel, browseGaiaLibraryLabel])

  const customEmptyStateLabels = useMemo(() => ({
    callout: emptyCalloutText,
    whatNext: whatNextLabel,
    browseGaiaLibrary: browseGaiaLibraryLabel,
  }), [emptyCalloutText, whatNextLabel, browseGaiaLibraryLabel])

  // ── Render ──────────────────────────────────────────────────────────
  // <PlaylistMenuStaticTextScope> mounts the `profilePlaylistMenuOptions`
  // static-text provider once for every row that renders a CustomMenu /
  // SystemMenu, replacing the per-row connectStaticText HOC + provider
  // that used to live inside each menu component.
  return (
    <div className="profile-my-playlists">
      <PlaylistMenuStaticTextScope>
      {/* Currently Watching — paints unconditionally so the row order
          Currently Watching → Watch Later → Favorites → custom → Watch
          History stays stable regardless of which data stream resolves first.
          `inProgressIsHydrating` (OR of the upstream carousel query +
          internal title-group hydration) drives the skeleton path; an empty
          result after both windows close renders `currentlyWatchingEmptyState`
          instead of unmounting. `skeletonCount=4` is the External-row default
          and lets the no-shell variant enter `PlaylistRow.Body`'s skeleton
          branch — see `PlaylistRow.tsx` for the fallback rule. */}
      {!isPreview ? (
        <ErrorBoundary FallbackComponent={CommunityErrorFallback} resetKeys={['currently-watching']}>
          <SystemPlaylistExternalRow
            playlistId="currently-watching"
            label={currentlyWatchingLabel}
            items={inProgressItems as unknown as CarouselItem[]}
            totalCount={inProgressItems.length}
            totalDuration={null}
            isHydrating={inProgressIsHydrating}
            onRemoveItem={handleHideCurrentlyWatching}
            customStats={currentlyWatchingCustomStats}
            seeMoreLabel={seeMoreLabel}
            showLessLabel={showLessLabel}
            seeEntireTemplate={parentSeeEntireTemplate}
            viewAllRoute="/currently-watching"
            editToolbarLabels={editToolbarLabels}
            isEditing={editingPlaylistId === 'currently-watching'}
            doneLabel={parentDoneLabel}
            onDoneEditing={handleDoneSystemEdit}
            onAction={handlePlaylistAction}
            showSkeletonSeriesLabel
            emptyState={currentlyWatchingEmptyState}
          />
        </ErrorBoundary>
      ) : null}

      {/* Private profile banner — owner-only nudge to flip visibility so
          shared playlists become discoverable. */}
      {isProfilePrivate ? (
        <PlaylistLibReviewBanner
          variant="otherProfile"
          buttonTo={loggedInUuid ? `/profile/${loggedInUuid}` : '/profile/edit'}
        />
      ) : null}

      {/* Watch Later */}
      {!isPreview && defaultPlaylist ? (
        <ErrorBoundary FallbackComponent={CommunityErrorFallback} resetKeys={[defaultPlaylist.id]}>
          <SystemPlaylistShellRow
            playlist={defaultPlaylist}
            label={watchLaterLabel}
            language={language}
            jwt={jwt}
            isEditing={editingPlaylistId === defaultPlaylist.id}
            doneLabel={parentDoneLabel}
            onDoneEditing={handleDoneSystemEdit}
            onRemoveItem={onRemoveWatchLaterItem ?? undefined}
            onReorderItem={onReorderWatchLaterItem ?? undefined}
            onAction={handlePlaylistAction}
            seeMoreLabel={seeMoreLabel}
            showLessLabel={showLessLabel}
            seeEntireTemplate={parentSeeEntireTemplate}
            viewAllRoute="/watch-later"
            videosLabel={parentZeroVideosLabel}
            zeroDurationLabel={parentZeroDurationLabel}
            editToolbarLabels={editToolbarLabels}
            emptyStateLabels={systemEmptyStateLabels}
            onWhatNextClick={handleWhatNextClick}
            onBrowseClick={handleBrowseClick}
          />
        </ErrorBoundary>
      ) : null}

      {/* Favorites */}
      {!isPreview && favoritesPlaylist ? (
        <ErrorBoundary
          FallbackComponent={CommunityErrorFallback}
          resetKeys={[favoritesPlaylist.id]}
        >
          <SystemPlaylistShellRow
            playlist={favoritesPlaylist}
            label={favoritesLabel}
            language={language}
            jwt={jwt}
            isEditing={editingPlaylistId === favoritesPlaylist.id}
            doneLabel={parentDoneLabel}
            onDoneEditing={handleDoneSystemEdit}
            onRemoveItem={onRemoveFavoritesItem ?? undefined}
            onReorderItem={onReorderFavoritesItem ?? undefined}
            onAction={handlePlaylistAction}
            seeMoreLabel={seeMoreLabel}
            showLessLabel={showLessLabel}
            seeEntireTemplate={parentSeeEntireTemplate}
            viewAllRoute="/favorites"
            videosLabel={parentZeroVideosLabel}
            zeroDurationLabel={parentZeroDurationLabel}
            editToolbarLabels={editToolbarLabels}
            emptyStateLabels={systemEmptyStateLabels}
            onWhatNextClick={handleWhatNextClick}
            onBrowseClick={handleBrowseClick}
          />
        </ErrorBoundary>
      ) : null}

      {/* Custom Playlists */}
      {filteredPlaylists.length > 0 ? filteredPlaylists.map((playlist) => {
        const isEditing = editingPlaylistId === playlist.id
        const rowNode = isEditing ? (
          <EditableMyPlaylistRow
            playlist={playlist}
            language={language}
            jwt={jwt}
            rowAuthorDisplay={null}
            allowedVisibilities={allowedVisibilities}
            editingName={editingName ?? playlist.playlistName ?? ''}
            onNameChange={handleNameChange}
            onDoneEditing={handleDoneEditing}
            onRemoveItem={handleRemoveCustomItem}
            onReorderItem={onReorderItem ?? undefined}
            onUpdateVisibility={onUpdateVisibility ?? undefined}
            seeMoreLabel={seeMoreLabel}
            showLessLabel={showLessLabel}
            seeEntireTemplate={parentSeeEntireTemplate}
            doneLabel={parentDoneLabel}
            editToolbarLabels={editToolbarLabels}
          />
        ) : (
          <MyPlaylistRow
            playlist={playlist}
            language={language}
            jwt={jwt}
            rowAuthorDisplay={null}
            allowedVisibilities={allowedVisibilities}
            onAction={handlePlaylistAction}
            onUpdateVisibility={onUpdateVisibility ?? undefined}
            onWhatNextClick={handleWhatNextClick}
            onBrowseClick={handleBrowseClick}
            seeMoreLabel={seeMoreLabel}
            showLessLabel={showLessLabel}
            seeEntireTemplate={parentSeeEntireTemplate}
            videosLabel={parentZeroVideosLabel}
            zeroDurationLabel={parentZeroDurationLabel}
            editToolbarLabels={editToolbarLabels}
            emptyStateLabels={customEmptyStateLabels}
          />
        )
        return (
          // Per-row error isolation — a crash in one row no longer takes down
          // the whole tab. resetKeys=[playlist.id] re-renders on row swap.
          <ErrorBoundary
            key={playlist.id}
            FallbackComponent={CommunityErrorFallback}
            resetKeys={[playlist.id]}
          >
            {rowNode}
          </ErrorBoundary>
        )
      }) : (
        <CommunityEmptyState
          variant="full-height"
          description={noPlaylistsText}
        />
      )}

      {/* Watch History */}
      {!isPreview && watchHistoryItems.length > 0 ? (
        <ErrorBoundary FallbackComponent={CommunityErrorFallback} resetKeys={['watch-history']}>
          <SystemPlaylistExternalRow
            playlistId="watch-history"
            label={watchHistoryLabel}
            items={watchHistoryItems as unknown as CarouselItem[]}
            totalCount={watchHistoryTotalCount}
            totalDuration={watchHistoryTotalDuration}
            isHydrating={false}
            onRemoveItem={handleRemoveWatchHistory}
            seeMoreLabel={seeMoreLabel}
            showLessLabel={showLessLabel}
            seeEntireTemplate={parentSeeEntireTemplate}
            viewAllRoute="/watch-history"
            editToolbarLabels={editToolbarLabels}
            isEditing={editingPlaylistId === 'watch-history'}
            doneLabel={parentDoneLabel}
            onDoneEditing={handleDoneSystemEdit}
            onAction={handlePlaylistAction}
          />
        </ErrorBoundary>
      ) : null}

      {/* No page-level <PlaylistSharePreview> — each row's PlaylistShareButton
          owns its own modal state. */}

      {pendingDelete && isBrowser() ? createPortal(
        <Suspense fallback={null}>
          <DeletePlaylistConfirmModal
            playlistName={pendingDelete.playlistName}
            itemCount={pendingDelete.itemCount}
            onConfirm={handleConfirmDelete}
            onCancel={handleCancelDelete}
          />
        </Suspense>,
        document.body,
      ) : null}
      </PlaylistMenuStaticTextScope>
    </div>
  )
}

function ProfileMyPlaylists (props: ProfileMyPlaylistsProps) {
  const { staticText, ...rest } = props
  return (
    <StaticTextProvider staticText={staticText}>
      <ProfileMyPlaylistsContent {...rest} />
    </StaticTextProvider>
  )
}

export default connectStaticText({ storeKey: 'profileMyPlaylists' })(React.memo(ProfileMyPlaylists))
